import React, { useEffect } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import {
  StageGateForm,
  StageGateFormActions,
} from "../../../schedulePlanning/components";
import { useScheduleEditor } from "../../../schedulePlanning";
import { useSearchParams } from "react-router-dom";

interface ContractorStageGateDrawerProps {
  stageGateId: number;
  projectId: string | undefined;
  handleClose: () => void;
}

export const ContractorStageGateDrawer: React.FC<
  ContractorStageGateDrawerProps
> = ({ stageGateId, projectId, handleClose }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    stageGateFormData,
    isStageGateCompleted,
    stageGateFormRef,
    stageGateDisabledConfig,
    editingScheduleItem,
    phaseOptions,
    getPhaseOptionsForEdit,
    isLoading,
  } = useScheduleEditor(Number(projectId));

  // Override disabled config for contractor - allow only description to be edited
  const contractorStageGateDisabledConfig = {
    ...stageGateDisabledConfig,
    title: true,
    description: false, // Allow contractor to edit description
    owner: true,
    userId: true,
    plannedCompletionDate: true,
    actualCompletionDate: true,
    parentPhaseId: true,
    parentPhase: true,
    plannedStartDate: true,
    plannedEndDate: true,
  };

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const currentId = params.get("id");
    const currentType = params.get("type");

    if (currentId !== String(stageGateId) || currentType !== "stageGate") {
      params.set("id", String(stageGateId));
      params.set("type", "stageGate");
      setSearchParams(params, { replace: true });
    }
  }, [stageGateId]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        width="100%"
        height="100%"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!stageGateFormData) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        width="100%"
        height="100%"
      >
        <Typography>No stage gate data</Typography>
      </Box>
    );
  }

  return (
    <Box height="100%" display="flex" flexDirection="column">
      {/* Form Content - Scrollable */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        <StageGateForm
          onSave={() => {}}
          onCancel={() => {}}
          ref={stageGateFormRef}
          initialData={stageGateFormData}
          isEditing={!!editingScheduleItem}
          phaseOptions={
            editingScheduleItem ? getPhaseOptionsForEdit : phaseOptions
          }
          stageGateId={stageGateId}
          projectId={projectId}
          stageGateDisabledConfig={contractorStageGateDisabledConfig}
          isStageGateCompleted={isStageGateCompleted}
        />
      </Box>

      {/* Action Buttons - Fixed at Bottom */}
      <Box
        sx={{
          padding: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          gap: 1,
          justifyContent: 'flex-end',
        }}
      >
        <StageGateFormActions
          onSave={() => stageGateFormRef.current?.submit()}
          onCancel={handleClose}
          isEditing={!!editingScheduleItem}
        />
      </Box>
    </Box>
  );
};
